/**
 * MZ Brake — vehicle finder (Year / Make / Model → vehicle page) and "my vehicle" store.
 *
 * Data comes from sections/mz-vehicle-data.liquid via the Section Rendering API and is cached
 * in localStorage for 12 hours. Rows are compact arrays (v5.0: no generation / chassis columns):
 *   [make, model, yearStart, yearEnd, handle, url, vehicleClass, displayName]
 *
 * Custom elements:
 *   <mz-vehicle-finder>  – cascading selects (Year → Make → Model, or Make → Model); the "go" control is a real <a> whose href is set to the
 *                          vehicle page, so navigation is a normal link click (no script redirects)
 *   <mz-vehicle-bar>     – header chip showing the saved vehicle
 *   <mz-fitment-check>   – on PDP: compares the saved vehicle with the product's fitment handles; when the page
 *                          only lists part of a long fitment list (v4.9) it asks the vehicle's collection
 *                          (sections/mz-fit-handles.liquid) whether the kit is in it
 *   <mz-fitment-list>    – on PDP (v4.9): "Show all" loads every fitted car from sections/mz-fitment-data.liquid,
 *                          grouped by make, with a search box
 *   <mz-make-models>     – make page (v4.9): every model of the make from the vehicle data, grouped A–Z with a
 *                          search box (v5.0)
 *   <mz-related-vehicles>– vehicle page (v4.9): models of the same make whose name starts with the same word,
 *                          closest model years first (v5.0)
 *
 * Events (on document): `mz:vehicle-changed` { detail: vehicle|null }
 */
(() => {
  const STORAGE_KEY = 'mz_vehicle';
  const CACHE_KEY = 'mz_vehicle_data_v4';
  const OLD_CACHE_KEYS = ['mz_vehicle_data_v3', 'mz_vehicle_data_v2', 'mz_vehicle_data'];
  const CACHE_TTL = 12 * 60 * 60 * 1000;
  const COL = { make: 0, model: 1, ys: 2, ye: 3, handle: 4, url: 5, cls: 6, name: 7 };

  /* ---------------------------------------------------------------- storage */
  const store = {
    get() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        return null;
      }
    },
    set(vehicle) {
      try {
        if (vehicle) localStorage.setItem(STORAGE_KEY, JSON.stringify(vehicle));
        else localStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        /* storage unavailable (private mode) */
      }
      document.dispatchEvent(new CustomEvent('mz:vehicle-changed', { detail: vehicle }));
    },
  };

  /* ------------------------------------------------------------- data load */
  let dataPromise = null;
  let dataPromiseScope = null;
  let configuredRoot = null;
  const rootUrl = () => configuredRoot || '/';
  const PROTOCOL = 2;
  const textValue = (value) => typeof value === 'string' && value.trim().length > 0;
  const wholeNumber = (value) => Number.isSafeInteger(value) && value >= 0;

  function sectionUrl(base, marker) {
    const url = new URL(base, window.location.origin);
    if (url.origin !== window.location.origin) throw new Error('cross-origin section URL');
    url.hash = '';
    url.searchParams.set('section_id', marker);
    const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url;
  }

  function scopeFor(url) {
    const preview = url.searchParams.get('preview_theme_id') || '';
    const theme = window.Shopify && window.Shopify.theme ? String(window.Shopify.theme.id || '') : '';
    return [url.origin, url.pathname, preview, theme].join('|');
  }

  function validVehicleRow(row, fitment) {
    const urlIndex = fitment ? 4 : COL.url;
    if (!Array.isArray(row) || row.length < (fitment ? 5 : 8)) return false;
    if (!textValue(row[0]) || !textValue(row[1]) || !textValue(row[urlIndex])) return false;
    if (!wholeNumber(row[2]) || !wholeNumber(row[3]) || (row[2] && row[3] && row[3] < row[2])) return false;
    if (!fitment && !textValue(row[COL.handle])) return false;
    try {
      return new URL(row[urlIndex], window.location.origin).origin === window.location.origin;
    } catch (error) {
      return false;
    }
  }

  function validCompleteRows(rows, total, validate, identity) {
    if (!wholeNumber(total) || !Array.isArray(rows) || rows.length !== total) return false;
    const seen = new Set();
    for (const row of rows) {
      if (!validate(row)) return false;
      const key = identity(row);
      if (seen.has(key)) return false;
      seen.add(key);
    }
    return true;
  }

  function readCache(scope) {
    try {
      OLD_CACHE_KEYS.forEach((key) => localStorage.removeItem(key));
      const parsed = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
      if (!parsed) return null;
      const valid = parsed.protocol === PROTOCOL && parsed.complete === true && parsed.scope === scope &&
        wholeNumber(parsed.t) && Date.now() >= parsed.t && Date.now() - parsed.t <= CACHE_TTL &&
        wholeNumber(parsed.pages) && parsed.pages === Math.max(1, Math.ceil(parsed.total / 250)) &&
        validCompleteRows(parsed.v, parsed.total, (row) => validVehicleRow(row, false), (row) => row[COL.handle]);
      if (!valid) {
        localStorage.removeItem(CACHE_KEY);
        return null;
      }
      return parsed.v;
    } catch (error) {
      try { localStorage.removeItem(CACHE_KEY); } catch (storageError) { void storageError; }
      return null;
    }
  }

  function writeCache(result, scope) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ protocol: PROTOCOL, complete: true, scope,
        t: Date.now(), total: result.total, pages: result.pages, v: result.rows }));
    } catch (error) {
      return;
    }
  }

  async function fetchSectionJson(url, marker) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const res = await fetch(String(url), { credentials: 'same-origin', signal: controller.signal });
      if (!res.ok) throw new Error(marker + ' ' + res.status);
      const markup = await res.text();
      const at = markup.indexOf('id="' + marker + '"');
      if (at < 0) throw new Error(marker + ' missing');
      const start = markup.indexOf('>', at);
      const end = markup.indexOf('</script', start + 1);
      if (start < 0 || end < 0) throw new Error(marker + ' incomplete payload');
      const json = JSON.parse(markup.slice(start + 1, end));
      if (!json || json.protocol !== PROTOCOL || json.error) throw new Error(marker + ' invalid protocol');
      return json;
    } finally {
      clearTimeout(timeout);
    }
  }

  async function loadPagedRows(base, marker, options) {
    let url = sectionUrl(base, marker);
    const select = options.select || ((payload) => payload);
    let initial = options.initial || null;
    const seenUrls = new Set();
    const seenRows = new Set();
    const rows = [];
    let baseline = null;
    let expectedPage = 1;
    while (url) {
      const requestUrl = String(url);
      if (seenUrls.has(requestUrl)) throw new Error(marker + ' repeated page URL');
      seenUrls.add(requestUrl);
      const payload = initial || await fetchSectionJson(requestUrl, marker);
      initial = null;
      const page = select(payload);
      if (!page || !wholeNumber(page.total) || !wholeNumber(page.pages) || !wholeNumber(page.page) ||
          !wholeNumber(page.offset) || !wholeNumber(page.page_size) || page.page_size !== 250 ||
          !textValue(page.page_param) || typeof page.next_url !== 'string' || !Array.isArray(page.v)) {
        throw new Error(marker + ' invalid pagination metadata');
      }
      const pages = Math.max(1, Math.ceil(page.total / page.page_size));
      if (page.pages !== pages && !(page.total === 0 && page.pages === 0)) throw new Error(marker + ' page count mismatch');
      if (page.page !== expectedPage || page.offset !== (expectedPage - 1) * page.page_size || page.page > pages) {
        throw new Error(marker + ' unexpected page or offset');
      }
      if (!baseline) baseline = { total: page.total, pages, pageParam: page.page_param, size: page.page_size };
      if (page.total !== baseline.total || pages !== baseline.pages || page.page_param !== baseline.pageParam || page.page_size !== baseline.size) {
        throw new Error(marker + ' catalog changed while loading');
      }
      const pageRows = page.v.filter((row) => row !== null);
      const expectedRows = Math.min(page.page_size, Math.max(0, page.total - page.offset));
      if (pageRows.length !== expectedRows) throw new Error(marker + ' incomplete page');
      for (const row of pageRows) {
        if (!options.validate(row)) throw new Error(marker + ' invalid row');
        const key = options.identity(row);
        if (seenRows.has(key)) throw new Error(marker + ' duplicate row or repeated page');
        seenRows.add(key);
        rows.push(row);
      }
      if (expectedPage < baseline.pages) {
        if (!page.next_url) throw new Error(marker + ' missing next page');
        const next = new URL(page.next_url, url);
        if (next.origin !== url.origin || next.pathname !== url.pathname ||
            next.searchParams.get(page.page_param) !== String(expectedPage + 1)) {
          throw new Error(marker + ' invalid next page URL');
        }
        next.searchParams.set('section_id', marker);
        const preview = url.searchParams.get('preview_theme_id');
        if (preview) next.searchParams.set('preview_theme_id', preview);
        next.hash = '';
        url = next;
        expectedPage += 1;
      } else {
        if (page.next_url) throw new Error(marker + ' unexpected extra page');
        url = null;
      }
    }
    if (!baseline || !validCompleteRows(rows, baseline.total, options.validate, options.identity)) {
      throw new Error(marker + ' incomplete catalog');
    }
    return { rows, total: baseline.total, pages: baseline.pages };
  }

  function loadData(root) {
    if (root) configuredRoot = root;
    const url = sectionUrl(rootUrl(), 'mz-vehicle-data');
    const scope = scopeFor(url);
    if (dataPromise && dataPromiseScope === scope) return dataPromise;
    dataPromiseScope = scope;
    const cached = readCache(scope);
    if (cached) {
      dataPromise = Promise.resolve(cached);
      return dataPromise;
    }
    const promise = loadPagedRows(url, 'mz-vehicle-data', {
      validate: (row) => validVehicleRow(row, false), identity: (row) => row[COL.handle],
    }).then((result) => {
      result.rows.sort((a, b) => a[COL.make].localeCompare(b[COL.make]) || a[COL.model].localeCompare(b[COL.model]) || b[COL.ys] - a[COL.ys]);
      writeCache(result, scope);
      document.dispatchEvent(new CustomEvent('mz:vehicle-data-ready'));
      return result.rows;
    }).catch((error) => {
      if (dataPromise === promise) dataPromise = null;
      throw error;
    });
    dataPromise = promise;
    return promise;
  }

  /* --------------------------------------------------------------- helpers */
  const uniq = (arr) => Array.from(new Set(arr));
  const rowToVehicle = (r) => ({
    handle: r[COL.handle],
    url: r[COL.url],
    make: r[COL.make],
    model: r[COL.model],
    yearStart: r[COL.ys],
    yearEnd: r[COL.ye],
    vehicleClass: r[COL.cls],
    name: r[COL.name],
  });
  /** v5.0: Model select options — one per car, value = the car's handle; years are added only where two cars of the
   *  list share a model name. */
  const modelOptions = (rows) => {
    const seen = {};
    rows.forEach((r) => (seen[r[COL.model]] = (seen[r[COL.model]] || 0) + 1));
    return rows
      .slice()
      .sort((a, b) => naturalCmp(a[COL.model], b[COL.model]) || b[COL.ys] - a[COL.ys])
      .map((r) => [r[COL.handle], seen[r[COL.model]] > 1 && r[COL.ys] ? r[COL.model] + ' (' + yearsText(r[COL.ys], r[COL.ye]) + ')' : r[COL.model]]);
  };

  /** v5.0: a car saved under an older name (names used to carry chassis codes) picks up the current name and link. */
  function syncSaved(rows) {
    const saved = store.get();
    if (!saved || !saved.handle) return;
    const row = rows.find((r) => r[COL.handle] === saved.handle);
    if (!row) return;
    const fresh = rowToVehicle(row);
    if (fresh.name !== saved.name || fresh.url !== saved.url || 'chassis' in saved || 'generation' in saved) store.set(fresh);
  }

  function fillSelect(select, values, placeholder) {
    const current = select.value;
    while (select.firstChild) select.removeChild(select.firstChild);
    const ph = document.createElement('option');
    ph.value = '';
    ph.textContent = placeholder;
    select.appendChild(ph);
    values.forEach(([value, label]) => {
      const opt = document.createElement('option');
      opt.value = value;
      opt.textContent = label;
      select.appendChild(opt);
    });
    select.disabled = values.length === 0;
    if (values.some(([v]) => v === current)) select.value = current;
  }

  function makeChip(text, className, href) {
    const el = document.createElement(href ? 'a' : 'span');
    el.className = 'mz-chip ' + className;
    el.textContent = text;
    if (href) el.setAttribute('href', href);
    return el;
  }

  /* ------------------------------------------------------------- v4.9 helpers */
  const idle = (fn, timeout) => ('requestIdleCallback' in window ? requestIdleCallback(fn, { timeout: timeout || 1200 }) : setTimeout(fn, 150));
  const naturalCmp = (a, b) => String(a || '').localeCompare(String(b || ''), undefined, { numeric: true, sensitivity: 'base' });
  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  /** v5.0: make-page group of a model — its first character (all digits share one group). */
  const groupKey = (model) => {
    const c = String(model || '').trim().charAt(0).toUpperCase();
    return /[0-9]/.test(c) ? '0–9' : c || '#';
  };
  /** Search box over <li data-text data-ys data-ye> items: every word must match; a 4-digit year must fall in the years. */
  const matches = (li, terms) =>
    terms.every((t) =>
      /^(19|20)\d\d$/.test(t) ? Number(li.dataset.ys) <= Number(t) && Number(t) <= Number(li.dataset.ye) : li.dataset.text.indexOf(t) >= 0
    );
  // v5.5: one model year (start = end) shows a single year, e.g. "2023" instead of "2023–2023"
  const yearsText = (ys, ye) => (ys && ye ? (String(ys) === String(ye) ? String(ys) : ys + '–' + ye) : '');

  const fitCache = {};
  function vehicleProducts(vehicle) {
    const base = vehicle.url || rootUrl() + 'collections/' + vehicle.handle;
    const url = sectionUrl(base, 'mz-fit-handles');
    const scope = scopeFor(url);
    const key = 'mz_fit_v2_' + vehicle.handle;
    const memoryKey = scope + '|' + key;
    if (fitCache[memoryKey]) return fitCache[memoryKey];
    try {
      sessionStorage.removeItem('mz_fit_' + vehicle.handle);
      const cached = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (cached && cached.protocol === PROTOCOL && cached.complete === true && cached.scope === scope &&
          wholeNumber(cached.pages) && cached.pages === Math.max(1, Math.ceil(cached.total / 250)) &&
          validCompleteRows(cached.v, cached.total, textValue, (handle) => handle)) {
        return (fitCache[memoryKey] = Promise.resolve(cached.v));
      }
      sessionStorage.removeItem(key);
    } catch (error) {
      try { sessionStorage.removeItem(key); } catch (storageError) { void storageError; }
    }
    const promise = loadPagedRows(url, 'mz-fit-handles', { validate: textValue, identity: (handle) => handle })
      .then((result) => {
        try {
          sessionStorage.setItem(key, JSON.stringify({ protocol: PROTOCOL, complete: true, scope,
            total: result.total, pages: result.pages, v: result.rows }));
        } catch (error) {
          void error;
        }
        return result.rows;
      }).catch((error) => {
        if (fitCache[memoryKey] === promise) delete fitCache[memoryKey];
        throw error;
      });
    fitCache[memoryKey] = promise;
    return promise;
  }

  async function productFitments(base, expectedTotal) {
    const url = sectionUrl(base, 'mz-fitment-data');
    const first = await fetchSectionJson(url, 'mz-fitment-data');
    if (!wholeNumber(first.total) || first.total !== expectedTotal || !Array.isArray(first.segments)) {
      throw new Error('fitment total mismatch');
    }
    const segments = first.segments.filter((segment) => segment !== null);
    const ids = segments.map((segment) => segment.id);
    const allowed = ['vehicle_applications', 'vehicle_applications_2', 'vehicle_applications_3'];
    if (new Set(ids).size !== ids.length || ids.some((id) => !allowed.includes(id)) ||
        segments.some((segment) => !wholeNumber(segment.total) || segment.total === 0) ||
        segments.reduce((sum, segment) => sum + segment.total, 0) !== first.total) {
      throw new Error('invalid fitment segments');
    }
    const results = await Promise.all(segments.map((segment) => loadPagedRows(url, 'mz-fitment-data', {
      initial: first,
      select(payload) {
        if (payload.protocol !== PROTOCOL || payload.total !== first.total || !Array.isArray(payload.segments)) {
          throw new Error('fitment data changed while loading');
        }
        const matches = payload.segments.filter((part) => part && part.id === segment.id);
        if (matches.length !== 1) throw new Error('missing fitment segment');
        return matches[0];
      },
      validate: (row) => validVehicleRow(row, true), identity: (row) => row[4],
    })));
    const rows = results.flatMap((result) => result.rows);
    if (!validCompleteRows(rows, first.total, (row) => validVehicleRow(row, true), (row) => row[4])) {
      throw new Error('duplicate or incomplete fitments');
    }
    return rows;
  }

  function sharedLabel(key, fallback) {
    const finder = document.querySelector('mz-vehicle-finder');
    return finder && finder.dataset[key] ? finder.dataset[key] : fallback;
  }

  /* -------------------------------------------------------- <mz-vehicle-finder> */
  class VehicleFinder extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.mode || 'year'; // 'year' | 'make'
      this.filterQuery = this.dataset.filterQuery || '';
      this.$year = this.querySelector('[data-role="year"]');
      this.$make = this.querySelector('[data-role="make"]');
      this.$model = this.querySelector('[data-role="model"]');
      this.$go = this.querySelector('[data-role="go"]');
      this.$status = this.querySelector('[data-role="status"]');
      this.$toggle = this.querySelector('[data-role="toggle-mode"]');
      this.$retry = this.querySelector('[data-role="retry"]');
      if (this.$retry) this.$retry.addEventListener('click', () => this.init());
      this.rows = [];
      this.t = (key, fallback) => (this.dataset[key] !== undefined ? this.dataset[key] : fallback);

      if (this.$year) this.$year.addEventListener('change', () => this.onYear());
      if (this.$make) this.$make.addEventListener('change', () => this.onMake());
      if (this.$model) this.$model.addEventListener('change', () => this.onModel());
      if (this.$go) this.$go.addEventListener('click', (e) => this.onGo(e));
      if (this.$toggle) {
        this.$toggle.addEventListener('click', (e) => {
          e.preventDefault();
          this.mode = this.mode === 'year' ? 'make' : 'year';
          this.applyMode();
          this.reset();
        });
      }
      this.applyMode();

      const start = () => this.init();
      if ('requestIdleCallback' in window) requestIdleCallback(start, { timeout: 1500 });
      else setTimeout(start, 200);
    }

    applyMode() {
      this.dataset.mode = this.mode;
      if (this.$year) this.$year.closest('.mz-finder__field').hidden = this.mode !== 'year';
      if (this.$toggle) this.$toggle.textContent = this.mode === 'year' ? this.t('labelBrowseByMake', 'Not sure of the year? Browse by make') : this.t('labelBrowseByYear', 'Search by year instead');
    }

    setStatus(text, kind) {
      if (!this.$status) return;
      this.$status.textContent = text || '';
      this.$status.dataset.kind = kind || '';
    }

    /** Enables the go link with the vehicle page as href, or disables it. */
    setTarget(row) {
      this.selectedRow = row || null;
      if (!this.$go) return;
      if (row) {
        let target = row[COL.url];
        if (this.filterQuery) target += (target.indexOf('?') >= 0 ? '&' : '?') + this.filterQuery;
        this.$go.setAttribute('href', target);
        this.$go.removeAttribute('aria-disabled');
      } else {
        this.$go.setAttribute('href', '#');
        this.$go.setAttribute('aria-disabled', 'true');
      }
    }

    async init() {
      if (this.loading) return;
      this.loading = true;
      if (this.$retry) this.$retry.hidden = true;
      this.setStatus(this.t('labelLoading', 'Loading vehicles…'));
      try {
        this.rows = await loadData(this.dataset.root);
        syncSaved(this.rows);
        this.setStatus('');
        this.reset();
        this.classList.add('is-ready');
      } catch (err) {
        console.warn('[mz-vehicle-finder]', err);
        this.rows = [];
        this.classList.remove('is-ready');
        [this.$year, this.$make, this.$model].forEach((select) => { if (select) select.disabled = true; });
        this.setTarget(null);
        this.setStatus(this.t('labelError', 'Could not load the complete car list. Try again, or browse by make below.'), 'error');
        if (this.$retry) this.$retry.hidden = false;
      } finally {
        this.loading = false;
      }
    }

    reset() {
      this.setTarget(null);
      if (this.mode === 'year') {
        const years = uniq(this.rows.flatMap((r) => (r[COL.ys] && r[COL.ye] ? [r[COL.ys], r[COL.ye]] : [])));
        const min = Math.min.apply(null, years);
        const max = Math.max.apply(null, years);
        const list = [];
        if (isFinite(min) && isFinite(max)) for (let y = max; y >= min; y--) list.push([String(y), String(y)]);
        fillSelect(this.$year, list, this.t('labelYear', 'Year'));
        fillSelect(this.$make, [], this.t('labelMake', 'Make'));
      } else {
        fillSelect(this.$make, uniq(this.rows.map((r) => r[COL.make])).map((m) => [m, m]), this.t('labelMake', 'Make'));
      }
      fillSelect(this.$model, [], this.t('labelModel', 'Model'));
    }

    candidates() {
      const year = this.mode === 'year' ? Number(this.$year.value) : null;
      return this.rows.filter((r) => !year || (r[COL.ys] <= year && r[COL.ye] >= year));
    }

    onYear() {
      const rows = this.candidates();
      fillSelect(this.$make, uniq(rows.map((r) => r[COL.make])).map((m) => [m, m]), this.t('labelMake', 'Make'));
      fillSelect(this.$model, [], this.t('labelModel', 'Model'));
      this.setTarget(null);
    }

    onMake() {
      const make = this.$make.value;
      const rows = this.candidates().filter((r) => r[COL.make] === make);
      fillSelect(this.$model, modelOptions(rows), this.t('labelModel', 'Model'));
      this.setTarget(null);
    }

    /** v5.0: the Model option is the car itself (value = handle) — no Generation / chassis step. */
    onModel() {
      const handle = this.$model.value;
      this.setTarget((handle && this.candidates().find((r) => r[COL.handle] === handle)) || null);
    }

    /** Saves the selection; the browser then follows the link's href like any other link. */
    onGo(event) {
      if (!this.selectedRow) {
        event.preventDefault();
        return;
      }
      const vehicle = rowToVehicle(this.selectedRow);
      store.set(vehicle);
      this.dispatchEvent(new CustomEvent('mz:finder-go', { bubbles: true, detail: vehicle }));
      if (this.dataset.noNavigate === 'true') event.preventDefault();
    }
  }

  /* ------------------------------------------------------------ <mz-vehicle-bar> */
  class VehicleBar extends HTMLElement {
    connectedCallback() {
      this.$label = this.querySelector('[data-role="label"]');
      this.$clear = this.querySelector('[data-role="clear"]');
      this.$link = this.querySelector('[data-role="link"]');
      if (this.$clear) {
        this.$clear.addEventListener('click', (e) => {
          e.preventDefault();
          store.set(null);
        });
      }
      this.render(store.get());
      document.addEventListener('mz:vehicle-changed', (e) => this.render(e.detail));
    }
    render(vehicle) {
      this.dataset.hasVehicle = vehicle ? 'true' : 'false';
      if (!this.$label) return;
      if (vehicle) {
        this.$label.textContent = vehicle.name || vehicle.make + ' ' + vehicle.model;
        if (this.$link) this.$link.setAttribute('href', vehicle.url);
      } else {
        this.$label.textContent = this.dataset.emptyLabel || 'Select your vehicle';
        if (this.$link) this.$link.setAttribute('href', this.dataset.hubUrl || '#');
      }
    }
  }

  /* ---------------------------------------------------------- <mz-fitment-check> */
  class FitmentCheck extends HTMLElement {
    connectedCallback() {
      this.handles = (this.dataset.handles || '').split(',').map((s) => s.trim()).filter(Boolean);
      this.total = parseInt(this.dataset.total || '0', 10) || this.handles.length;
      this.product = this.dataset.product || '';
      this.$out = this.querySelector('[data-role="result"]');
      this.render(store.get());
      document.addEventListener('mz:vehicle-changed', (e) => this.render(e.detail));
    }
    show(text, className, href) {
      while (this.$out.firstChild) this.$out.removeChild(this.$out.firstChild);
      this.$out.appendChild(makeChip(text, className, href));
    }
    async render(vehicle) {
      if (!this.$out) return;
      const token = (this.token = (this.token || 0) + 1);
      if (!vehicle) {
        this.dataset.state = 'none';
        this.show(this.dataset.labelSelect || 'Select your vehicle to check fitment', 'mz-chip--muted', this.dataset.hubUrl || '#');
        return;
      }
      let fits = this.handles.indexOf(vehicle.handle) >= 0;
      if (!fits && this.product && this.total > this.handles.length) {
        // v4.9: the page lists only part of a long fitment list → ask the vehicle's collection
        this.dataset.state = 'checking';
        this.show(this.dataset.labelChecking || 'Checking fit…', 'mz-chip--muted');
        try {
          const list = await vehicleProducts(vehicle);
          if (token !== this.token) return;
          fits = list.indexOf(this.product) >= 0;
        } catch (err) {
          if (token !== this.token) return;
          console.warn('[mz-fitment-check]', err);
          this.dataset.state = 'error';
          this.show(sharedLabel('labelFitError', 'Could not check fitment. Try again.'), 'mz-chip--warning');
          const retry = el('button', 'mz-link', sharedLabel('labelRetry', 'Try again'));
          retry.type = 'button';
          retry.addEventListener('click', () => this.render(store.get()));
          this.$out.appendChild(retry);
          return;
        }
      }
      this.dataset.state = fits ? 'fits' : 'unknown';
      const name = vehicle.name || vehicle.make + ' ' + vehicle.model;
      const template = fits ? this.dataset.labelFits || 'Fits your {vehicle}' : this.dataset.labelUnknown || 'Not listed for your {vehicle} — ask us';
      this.show((fits ? '✓ ' : '') + template.replace('{vehicle}', name), fits ? 'mz-chip--success' : 'mz-chip--warning');
    }
  }

  /* ---------------------------------------------------------- <mz-fitment-list> (v4.9) */
  // Product page: Liquid renders the first 50 cars; "Show all" loads every car (sections/mz-fitment-data.liquid,
  // 250 per page per list) and shows them grouped by make with a search box (make, model or a year).
  class FitmentList extends HTMLElement {
    connectedCallback() {
      this.$list = this.querySelector('[data-role="list"]');
      this.$more = this.querySelector('[data-role="more"]');
      this.total = parseInt(this.dataset.total || '0', 10);
      if (this.$more) this.$more.addEventListener('click', () => this.showAll());
    }
    async showAll() {
      const rendered = this.$list ? this.$list.children.length : 0;
      if (!this.dataset.endpoint || this.total <= rendered) {
        this.querySelectorAll('[data-overflow]').forEach((li) => (li.hidden = false));
        if (this.$more) this.$more.remove();
        return;
      }
      const label = this.$more.textContent;
      this.$more.disabled = true;
      this.$more.textContent = this.dataset.labelLoading || 'Loading…';
      try {
        const rows = await productFitments(this.dataset.endpoint, this.total);
        this.renderAll(rows);
      } catch (err) {
        console.warn('[mz-fitment-list]', err);
        this.$more.disabled = false;
        this.$more.textContent = this.dataset.labelError || label;
      }
    }
    renderAll(rows) {
      // row: [make, model, yearStart, yearEnd, url]  (v5.0: no chassis column)
      const byMake = new Map();
      rows.forEach((r) => {
        if (!byMake.has(r[0])) byMake.set(r[0], []);
        byMake.get(r[0]).push(r);
      });
      const wrap = el('div', 'mz-fitment__all');
      const search = el('input', 'mz-fitment__search');
      search.type = 'search';
      search.placeholder = this.dataset.labelSearch || 'Search make, model or year';
      search.setAttribute('aria-label', search.placeholder);
      wrap.appendChild(search);
      const groups = [];
      Array.from(byMake.keys())
        .sort(naturalCmp)
        .forEach((make) => {
          const items = byMake.get(make).sort((a, b) => naturalCmp(a[1], b[1]) || b[2] - a[2]);
          const det = el('details', 'mz-fitment__group');
          det.appendChild(el('summary', '', make));   // v5.1: no vehicle counts
          const ul = el('ul', 'mz-fitment__list');
          ul.setAttribute('role', 'list');
          items.forEach((r) => {
            const li = el('li', 'mz-fitment__item');
            const a = el('a', 'mz-fitment__link');
            a.href = r[4];
            a.append(el('span', 'mz-fitment__name', make + ' ' + r[1]), el('span', 'mz-fitment__meta mz-muted', yearsText(r[2], r[3])));
            li.appendChild(a);
            li.dataset.text = (make + ' ' + r[1]).toLowerCase();
            li.dataset.ys = r[2] || 0;
            li.dataset.ye = r[3] || 0;
            ul.appendChild(li);
          });
          det.appendChild(ul);
          wrap.appendChild(det);
          groups.push(det);
        });
      const empty = el('p', 'mz-fitment__empty mz-muted', this.dataset.labelNone || 'No car matches that search.');
      empty.hidden = true;
      wrap.appendChild(empty);
      search.addEventListener('input', () => {
        const terms = search.value.trim().toLowerCase().split(/\s+/).filter(Boolean);
        let any = false;
        groups.forEach((det) => {
          let hits = 0;
          det.querySelectorAll('li').forEach((li) => {
            const ok = matches(li, terms);
            li.hidden = !ok;
            if (ok) hits += 1;
          });
          det.hidden = hits === 0;
          det.open = terms.length > 0 && hits > 0 && hits <= 60;
          if (hits) any = true;
        });
        empty.hidden = any;
      });
      if (this.$list) this.$list.replaceWith(wrap);
      if (this.$more) this.$more.remove();
      search.focus();
    }
  }

  /* ----------------------------------------------------------- <mz-make-models> (v4.9 · v5.0) */
  // Make page: Liquid can show only the first 50 vehicles of the make; this renders all of them from the vehicle data
  // (already cached by the finder). v5.0: grouped A–Z by the first character of the model name (no chassis codes),
  // with a search box once the make has more than 12 models.
  class MakeModels extends HTMLElement {
    connectedCallback() {
      this.onDataReady = () => { if (this.dataset.complete !== 'true') this.init(); };
      document.addEventListener('mz:vehicle-data-ready', this.onDataReady);
      idle(() => this.init());
    }
    disconnectedCallback() {
      document.removeEventListener('mz:vehicle-data-ready', this.onDataReady);
    }
    async init() {
      if (this.loading || this.dataset.complete === 'true') return;
      const make = this.dataset.make;
      const $list = this.querySelector('[data-role="list"]');
      if (!make || !$list) return;
      let rows;
      this.loading = true;
      try {
        rows = (await loadData(this.dataset.root)).filter((r) => r[COL.make] === make);
      } catch (err) {
        console.warn('[mz-make-models]', err);
        return;
      } finally {
        this.loading = false;
      }
      if (!rows.length) return;
      const groups = new Map();
      rows.forEach((r) => {
        const key = groupKey(r[COL.model]);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(r);
      });
      const frag = document.createDocumentFragment();
      const cards = [];
      Array.from(groups.keys())
        .sort(naturalCmp)
        .forEach((key) => {
          const card = el('li', 'mz-make__model');
          card.appendChild(el('h3', 'mz-make__model-name', key));
          const ul = el('ul', 'mz-make__gens');
          ul.setAttribute('role', 'list');
          groups
            .get(key)
            .sort((a, b) => naturalCmp(a[COL.model], b[COL.model]) || b[COL.ys] - a[COL.ys])
            .forEach((r) => {
              const li = el('li');
              const a = el('a', 'mz-chip', r[COL.model] + (r[COL.ys] ? ' · ' + yearsText(r[COL.ys], r[COL.ye]) : ''));
              a.href = r[COL.url];
              li.dataset.text = String(r[COL.model]).toLowerCase();
              li.dataset.ys = r[COL.ys] || 0;
              li.dataset.ye = r[COL.ye] || 0;
              li.appendChild(a);
              ul.appendChild(li);
            });
          card.appendChild(ul);
          frag.appendChild(card);
          cards.push(card);
        });
      while ($list.firstChild) $list.removeChild($list.firstChild);
      $list.appendChild(frag);
      if (rows.length > 12 && !this.querySelector('.mz-make__search')) {
        const search = el('input', 'mz-make__search');
        search.type = 'search';
        search.placeholder = this.dataset.labelSearch || 'Search models or a year';
        search.setAttribute('aria-label', search.placeholder);
        const empty = el('p', 'mz-muted mz-make__empty', this.dataset.labelNone || 'No model matches that search.');
        empty.hidden = true;
        search.addEventListener('input', () => {
          const terms = search.value.trim().toLowerCase().split(/\s+/).filter(Boolean);
          let any = false;
          cards.forEach((card) => {
            let hits = 0;
            card.querySelectorAll('li').forEach((li) => {
              const ok = matches(li, terms);
              li.hidden = !ok;
              if (ok) hits += 1;
            });
            card.hidden = hits === 0;
            if (hits) any = true;
          });
          empty.hidden = any;
        });
        $list.before(search);
        $list.after(empty);
      }
      // v5.2: no model counts on the make page
      this.querySelectorAll('[data-role="partial"]').forEach((n) => n.remove());
      this.dataset.complete = 'true';
    }
  }

  /* ------------------------------------------------------ <mz-related-vehicles> (v4.9 · v5.0) */
  // Vehicle page: other cars of the same make whose model name starts with the same word (v5.0: no chassis codes),
  // the ones whose model years overlap or sit closest to this car first.
  class RelatedVehicles extends HTMLElement {
    connectedCallback() {
      this.onDataReady = () => { if (this.dataset.complete !== 'true') this.init(); };
      document.addEventListener('mz:vehicle-data-ready', this.onDataReady);
      idle(() => this.init(), 2000);
    }
    disconnectedCallback() {
      document.removeEventListener('mz:vehicle-data-ready', this.onDataReady);
    }
    async init() {
      if (this.loading || this.dataset.complete === 'true') return;
      const { make, model, handle } = this.dataset;
      const ys = parseInt(this.dataset.ys || '0', 10);
      const ye = parseInt(this.dataset.ye || '0', 10);
      const limit = parseInt(this.dataset.limit || '8', 10);
      const $list = this.querySelector('[data-role="list"]');
      const stem = String(model || '').split(' ')[0].toLowerCase();
      if (!make || !stem || !$list) return;
      let rows;
      this.loading = true;
      try {
        rows = (await loadData(this.dataset.root)).filter((r) => r[COL.make] === make && r[COL.handle] !== handle);
      } catch (err) {
        return;
      } finally {
        this.loading = false;
      }
      const gap = (r) => (ys && ye && r[COL.ys] && r[COL.ye] ? Math.max(0, r[COL.ys] - ye, ys - r[COL.ye]) : 0);
      const pick = rows
        .filter((r) => String(r[COL.model]).split(' ')[0].toLowerCase() === stem)
        .sort((a, b) => gap(a) - gap(b) || naturalCmp(a[COL.model], b[COL.model]) || b[COL.ys] - a[COL.ys])
        .slice(0, limit);
      this.dataset.complete = 'true';
      if (!pick.length) return;
      pick.forEach((r) => {
        const li = el('li');
        const a = el('a', 'mz-card mz-card--link');
        a.href = r[COL.url];
        a.append(el('span', 'mz-card__title mz-vcontent__related-title', r[COL.model]), el('span', 'mz-card__text', yearsText(r[COL.ys], r[COL.ye])));
        li.appendChild(a);
        $list.appendChild(li);
      });
      this.hidden = false;
    }
  }

  if (!customElements.get('mz-vehicle-finder')) customElements.define('mz-vehicle-finder', VehicleFinder);
  if (!customElements.get('mz-vehicle-bar')) customElements.define('mz-vehicle-bar', VehicleBar);
  if (!customElements.get('mz-fitment-check')) customElements.define('mz-fitment-check', FitmentCheck);
  if (!customElements.get('mz-fitment-list')) customElements.define('mz-fitment-list', FitmentList);
  if (!customElements.get('mz-make-models')) customElements.define('mz-make-models', MakeModels);
  if (!customElements.get('mz-related-vehicles')) customElements.define('mz-related-vehicles', RelatedVehicles);

  window.MZVehicle = { store: store, loadData: loadData };
})();

